import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Scissors, ShieldCheck, Sparkles, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import { LandingHeader } from "@/components/LandingHeader";
import { LandingContact } from "@/components/LandingContact";
import { Gallery } from "@/components/Gallery";
import { Footer } from "@/components/Footer";
import { SHOP_PHOTO_WIDE, IMG_GROOM } from "@/i18n/translations";
import { Seo } from "@/components/Seo";
import { track } from "@/lib/analytics";

const BookBtn = ({ label, testid, onClick, variant }) => (
  <Button
    data-testid={testid}
    onClick={onClick}
    className={
      variant === "outline"
        ? "rounded-full border-2 border-brand bg-transparent text-brand hover:bg-brand hover:text-brand-cream font-bold h-12 px-7 py-6 text-base no-a11y-underline group"
        : "rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-12 px-7 py-6 text-base no-a11y-underline group"
    }
  >
    {label}
    <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
  </Button>
);

export default function LandingGrooming() {
  const { t } = useLang();
  const g = t.lg;
  const navigate = useNavigate();

  const goBook = (source) => {
    track("landing_cta", { fb_event: "Lead", landing: "grooming", source });
    navigate("/#grooming");
  };

  return (
    <div className="bg-background overflow-x-hidden">
      <Seo title={t.seo.grooming_title} description={t.seo.grooming_desc} path="/landing/grooming" />
      <LandingHeader backLabel={g.back} />

      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden pt-[72px]" data-testid="lg-hero">
        <div className="absolute inset-0">
          <img src={SHOP_PHOTO_WIDE} alt="Care For Me grooming" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-brand-ink/60" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-deep/75 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-16 w-full">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full glass-dark border border-white/20 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-stone">
              <Scissors className="h-3.5 w-3.5 text-bamboo" /> {g.badge}
            </span>
            <h1 className="mt-6 font-heading text-5xl sm:text-6xl font-medium leading-[1.05] text-stone tracking-tight">{g.hero_h1}</h1>
            <p className="mt-6 text-lg text-stone/85 leading-relaxed">{g.hero_p}</p>
            <div className="mt-8">
              <BookBtn label={g.cta_book} testid="lg-hero-cta" onClick={() => goBook("hero")} />
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-0 inset-x-0 h-20 bg-gradient-to-t from-background to-transparent z-10" />
      </section>

      {/* Includes */}
      <section className="py-24 sm:py-28" data-testid="lg-includes">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <div className="rounded-[2rem] overflow-hidden shadow-[0_20px_60px_rgba(42,37,34,0.18)] border-4 border-white">
              <img src={IMG_GROOM} alt="Groomed dog" className="w-full h-[440px] object-cover object-top" />
            </div>
          </motion.div>
          <div>
            <h2 className="font-heading text-4xl sm:text-5xl font-medium text-brand-ink tracking-tight">{g.includes_h2}</h2>
            <ul className="mt-8 grid sm:grid-cols-2 gap-3">
              {g.includes.map((item, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-center gap-3 rounded-2xl bg-white border border-brand/10 p-4"
                  data-testid={`lg-include-${i}`}
                >
                  <span className="grid place-items-center h-8 w-8 rounded-full bg-bamboo-soft text-bamboo-deep shrink-0"><Check className="h-4 w-4" /></span>
                  <span className="font-semibold text-brand-ink text-sm">{item}</span>
                </motion.li>
              ))}
            </ul>
            <div className="mt-8">
              <BookBtn label={g.cta_request} testid="lg-includes-cta" onClick={() => goBook("includes")} />
            </div>
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="py-24 sm:py-28 bg-brand text-brand-cream" data-testid="lg-why">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <h2 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight text-center">{g.why_h2}</h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {g.why.map((w, i) => {
              const icons = [ShieldCheck, ShieldCheck, Sparkles, Leaf, Check];
              const Icon = icons[i % icons.length];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="rounded-2xl bg-white/8 border border-white/12 p-5 text-center"
                  data-testid={`lg-why-${i}`}
                >
                  <span className="mx-auto grid place-items-center h-11 w-11 rounded-full bg-bamboo/25 text-bamboo-soft mb-3"><Icon className="h-5 w-5" /></span>
                  <p className="font-semibold text-sm">{w}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Space + Gallery */}
      <Gallery />

      {/* Contact + hours */}
      <LandingContact heading={g.contact_h2} />

      <Footer />
    </div>
  );
}
